import { useState, useEffect } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const LogoMark = () => (
  <svg width="26" height="26" viewBox="0 0 32 32" fill="none" aria-hidden="true">
    <rect x="1" y="1" width="30" height="30" stroke="#00E5FF" strokeWidth="1.5" />
    <path d="M9 9v14h11" stroke="#00E5FF" strokeWidth="2" />
    <circle cx="22" cy="10" r="2.5" fill="#00E5FF" />
  </svg>
);

export default function Navbar() {
  const { t, lang, toggleLang } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const links = [
    { to: "/", label: t.nav.home },
    { to: "/about", label: t.nav.about },
    { to: "/solutions", label: t.nav.solutions },
    { to: "/case-studies", label: t.nav.cases },
    { to: "/insights", label: t.nav.insights },
    { to: "/ecosystem", label: t.nav.portal },
    { to: "/contact", label: t.nav.contact },
  ];

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-navy/80 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <nav className="max-w-[1320px] mx-auto px-6 lg:px-10 h-[76px] flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
          <LogoMark />
          <span className="font-display font-semibold tracking-tight text-lg text-[#F0F4F8]">
            Litterantara
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.to === "/" ? "home" : l.to.slice(1)}`}
              className={({ isActive }) =>
                `font-mono-tech text-[12px] uppercase tracking-widest transition-colors ${
                  isActive ? "text-cyan-precision" : "text-[#94A3B8] hover:text-[#F0F4F8]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <button
            data-testid="lang-toggle"
            onClick={toggleLang}
            className="font-mono-tech text-[12px] uppercase tracking-widest text-[#94A3B8] hover:text-cyan-precision border border-white/10 hover:border-cyan-precision/50 px-3 py-2 transition-colors"
            aria-label="Toggle language"
          >
            {lang === "id" ? "ID / EN" : "EN / ID"}
          </button>

          <button
            data-testid="nav-cta"
            onClick={() => navigate("/contact")}
            className="hidden sm:inline-flex items-center gap-1.5 font-mono-tech text-[12px] uppercase tracking-widest border border-cyan-precision text-cyan-precision px-4 py-2.5 hover:bg-cyan-precision hover:text-navy transition-colors"
          >
            {t.nav.cta} <ArrowUpRight size={14} />
          </button>

          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-[#F0F4F8] p-2"
            onClick={() => setOpen((o) => !o)}
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden overflow-hidden bg-navy/95 backdrop-blur-xl border-b border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-nav-${l.to === "/" ? "home" : l.to.slice(1)}`}
                  className={({ isActive }) =>
                    `font-display text-xl ${isActive ? "text-cyan-precision" : "text-[#F0F4F8]"}`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <button
                data-testid="mobile-nav-cta"
                onClick={() => navigate("/contact")}
                className="mt-2 inline-flex items-center justify-center gap-1.5 font-mono-tech text-[12px] uppercase tracking-widest border border-cyan-precision text-cyan-precision px-4 py-3 hover:bg-cyan-precision hover:text-navy transition-colors"
              >
                {t.nav.cta} <ArrowUpRight size={14} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
